import React, { useEffect, useState } from "react";
import { getAllWarmUp } from "../services/AssessmentService";
import AssessmentSection from "../components/AssessmentSection";
import { Button, Card, Chip, EmptyState, SectionHeading } from "../components/ui";
import { Stagger, StaggerItem } from "../components/ui/motion";
import { ArrowLeft, Play, Quiz } from "../components/icons";
import campusCareMark from "../assets/images/campuscare-mark.webp";

/**
 * Warm ups — short, retakeable practice sets.
 *
 * Rendered as a strip of cards rather than a page of panels: a warm up is a
 * small commitment, and the card should look like one. The indigo top edge
 * marks the whole family as practice, distinct from graded assessments.
 *
 * `embedded` keeps this usable as a dashboard band; standalone it fills the
 * section workspace. Behaviour is unchanged — starting one swaps the list for
 * the existing AssessmentSection, which owns scoring and submission.
 */
export const Warm = ({ embedded = false }) => {
  const [warmups, setWarmups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await getAllWarmUp();
        setWarmups(response?.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (active) {
    return (
      <div>
        <div style={{ marginBottom: "var(--s-4)" }}>
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => setActive(null)}>
            Back to warm ups
          </Button>
        </div>
        <AssessmentSection
          type="warmup"
          selectedModule={active}
          goBack={() => setActive(null)}
        />
      </div>
    );
  }

  return (
    <section className={embedded ? "ds-section" : undefined}>
      <SectionHeading
        title="Warm up"
        description="Quick practice sets. Unlimited retakes — they don't affect your record."
      />

      {loading ? (
        <p className="ds-sub">Loading warm ups…</p>
      ) : warmups.length === 0 ? (
        <Card>
          <EmptyState
            icon={Quiz}
            text="No warm ups have been published for you yet."
          />
        </Card>
      ) : (
        <div className="ds-warmup">
        <Stagger className="ds-grid ds-grid--2">
          {warmups.map((warmup) => (
            <StaggerItem key={warmup._id}>
            <Card accent pad className="ds-stack">
              <Chip>Warm up</Chip>
              <h3 className="ds-h3 ds-mt-3" style={{ lineHeight: 1.4 }}>
                {warmup.name}
              </h3>
              <p className="ds-sub ds-row-flex ds-gap-2" style={{ marginTop: 6 }}>
                <Quiz size={14} />
                {warmup.questions?.length || 0} question
                {warmup.questions?.length === 1 ? "" : "s"}
              </p>
              <div className="ds-mt-4" style={{ marginTop: "auto", paddingTop: "var(--s-4)" }}>
                <Button
                  block
                  icon={Play}
                  onClick={() =>
                    setActive({ ...warmup, title: warmup.name || "Warm up" })
                  }
                >
                  Start
                </Button>
              </div>
            </Card>
            </StaggerItem>
          ))}
        </Stagger>

        <Card className="ds-warmup__aside">
          <img
            src={campusCareMark}
            alt=""
            className="ds-warmup__mark"
            width={60}
            height={60}
            loading="lazy"
          />
          <div>
            <h3 className="ds-h3">Practice, not assessment</h3>
            <p className="ds-sub">
              Warm ups are yours to repeat. Scores here never count towards your
              record — use them to find the gaps before an assessment does.
            </p>
          </div>
        </Card>
        </div>
      )}
    </section>
  );
};

export default Warm;
